import { useState } from 'react';
import axios from 'axios';
import { Check, Copy, KeyRound } from 'lucide-react';
import { ConfirmDialog } from '../../ConfirmDialog';
import { formatShortWhen } from '../../formatWhen';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { TranscriberKeyInfo } from './transcriptsApi';

// THE WORKER'S KEY, issued here because this is the tab it serves. A script in a terminal
// cannot do a Google sign-in, so it proves who it is with this instead; the server keeps
// only a hash, which is why the key is readable once — the moment it is made — and after
// that only its last four characters.

function CopyLine({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-start gap-2 rounded-lg bg-slate-900 px-3 py-2">
      <code className="min-w-0 flex-1 break-all font-mono text-xs text-slate-100">{text}</code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
        className="shrink-0 text-slate-400 hover:text-white"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function TranscriberKey({ info, onChange }: {
  info: TranscriberKeyInfo | null;
  onChange: () => void;
}) {
  const { t, language } = useLanguage();
  const [fresh, setFresh] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setError(null);
    try {
      const { data } = await axios.post<{ token: string }>('/api/notice/transcripts/key');
      setFresh(data.token);
      onChange();
    } catch {
      setError(t('notice.transcripts.keyError'));
    }
  };

  const revoke = async () => {
    setRevoking(false);
    setFresh(null);
    try {
      await axios.delete('/api/notice/transcripts/key');
    } catch {
      setError(t('notice.transcripts.keyError'));
    }
    onChange();
  };

  const link = 'font-semibold text-blue-600/80 hover:underline';

  return (
    <div className={`flex flex-col items-center gap-2 text-center ${fresh ? 'w-full' : ''}`}>
      {fresh ? (
        <div className="flex w-full flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-left">
          <p className="font-semibold text-amber-800">{t('notice.transcripts.keyOnce')}</p>
          <CopyLine text={fresh} />
          <p className="text-slate-600">{t('notice.transcripts.keyRun')}</p>
          <CopyLine text={`.venv/bin/python noticeia_transcriber.py --worker --key ${fresh}`} />
        </div>
      ) : info ? (
        <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span
            className="inline-flex items-center gap-1"
            title={info.createdAt ? formatShortWhen(info.createdAt, language) : undefined}
          >
            <KeyRound className="h-3.5 w-3.5" />
            {t('notice.transcripts.keyHave').replace('{preview}', info.preview ?? '')}
          </span>
          <button type="button" className={link} onClick={() => void generate()}>
            {t('notice.transcripts.keyReplace')}
          </button>
          <button type="button" className={link} onClick={() => setRevoking(true)}>
            {t('notice.transcripts.keyRevoke')}
          </button>
        </p>
      ) : (
        <button
          type="button"
          onClick={() => void generate()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          <KeyRound className="h-4 w-4" />
          {t('notice.transcripts.keyGenerate')}
        </button>
      )}
      {error && <p className="text-rose-600">{error}</p>}

      {revoking && (
        <ConfirmDialog
          title={t('notice.transcripts.keyRevokeConfirm.title')}
          body={t('notice.transcripts.keyRevokeConfirm.body')}
          confirmLabel={t('notice.transcripts.keyRevoke')}
          cancelLabel={t('notice.catalog.cancel')}
          onConfirm={() => void revoke()}
          onCancel={() => setRevoking(false)}
        />
      )}
    </div>
  );
}
